import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {BehaviorSubject, merge, Observable, of, Subject, Subscription} from 'rxjs';
import {Config, DataType, RefType} from 'smag-commons/cfg/connector/iec61850';
import {
    DEFAULT_POLLING_INTERVAL,
    EDITOR_BUILTIN_FUNCTIONS,
    EDITOR_BUILTIN_TYPES,
    IEC61850_DATA_TYPES,
    IEC61850_DEFAULT_DEVICE_DATA_TYPE,
    IEC61850_DEFAULT_DEVICE_REF_TYPE,
    IEC61850_DEFAULT_HOST_PORT,
    IEC61850_REF_TYPES
} from '../connectors';
import * as _ from 'lodash';
import {isBlank, keySorter} from '../../../commons/commons';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {
    validateGt,
    validateInteger,
    validateNotBlank,
    validateNotBlankTrim,
    validateNotDuplicate
} from '../../../commons/validators';
import {
    PollingPolicy,
    PollingPolicyComponent,
    PollingPolicyData,
    setDevicePolicy
} from '../polling-policy/polling-policy.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {Features} from '../expression-editor/expression-editor.component';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';

interface HostConfig {
    hostId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface DeviceConfig {
    devId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface PagedHosts {
    data: HostConfig[];
    total: number;
}

interface PagedDevices {
    data: DeviceConfig[];
    total: number;
}

const VARIABLE_ACCESSORS = ['r', 'v']; // "references", "variable" (command)

const HOST_ADD_DIALOG_MAX_WIDTH = 500;
const HOST_RENAME_DIALOG_MAX_WIDTH = 500;
const HOST_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADD_DIALOG_MAX_WIDTH = 500;
const DEVICE_RENAME_DIALOG_MAX_WIDTH = 500;
const DEVICE_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15, 30, 50];

const EXPRESSION_HELP = 'Input references are available through reference-value map "r". Command value is supplied in variable "v".__{iec61850}';

@Component({
    selector: 'app-iec61850-config',
    templateUrl: './iec61850-config.component.html',
    styleUrls: ['./iec61850-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Items per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class Iec61850ConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadDevsPages: Subject<void>;
    private reloadHostsPages: Subject<void>;
    @ViewChild('addHost') private addHostDialog: TemplateRef<any>;
    @ViewChild('renameHost') private renameHostDialog: TemplateRef<any>;
    @ViewChild('deleteHost') private deleteHostDialog: TemplateRef<any>;
    @ViewChild('addDevice') private addDeviceDialog: TemplateRef<any>;
    @ViewChild('renameDevice') private renameDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteDevice') private deleteDeviceDialog: TemplateRef<any>;
    @ViewChild('advancedDeviceSettings') private advancedDeviceSettingsDialog: TemplateRef<any>;
    @ViewChild('devsPaginator') private devsPaginator: MatPaginator;
    @ViewChild('hostsPaginator') private hostsPaginator: MatPaginator;

    pagedDevs: BehaviorSubject<PagedDevices>;
    pagedHosts: BehaviorSubject<PagedHosts>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    form: FormGroup;
    hosts: HostConfig[];
    devices: DeviceConfig[];

    // exported in HTML
    IEC61850_DATA_TYPES = IEC61850_DATA_TYPES;
    IEC61850_REF_TYPES = IEC61850_REF_TYPES;
    EXPRESSION_HELP = EXPRESSION_HELP;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private fb: FormBuilder,
                private readonly dialog: MatDialog,
                private ngZone: NgZone,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.reloadDevsPages = new Subject<void>();
        this.reloadHostsPages = new Subject<void>();
        this.pagedDevs = new BehaviorSubject<PagedDevices>(null);
        this.pagedHosts = new BehaviorSubject<PagedHosts>(null);
    }

    ngOnInit() {
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({});
        this.hosts = Object.keys(this.cfg.hosts).map(hostId => this.mkHostConfig(hostId));
        this.hosts.sort(keySorter('hostId'));
        this.devices = Object.keys(this.cfg.devices).map(devId => this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();
    }

    ngAfterViewInit() {
        merge(this.hostsPaginator.page,
            this.reloadHostsPages.pipe(tap(() => this.hostsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedHosts(this.hostsPaginator.pageSize, this.hostsPaginator.pageIndex).pipe(
                    startWith(null as PagedHosts) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedHosts => {
            if (pagedHosts) {
                this.pagedHosts.next(pagedHosts);
                this.changeDetectorRef.detectChanges();
            }
        });

        merge(this.devsPaginator.page,
            this.reloadDevsPages.pipe(tap(() => this.devsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedDevices(this.devsPaginator.pageSize, this.devsPaginator.pageIndex).pipe(
                    startWith(null as PagedDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedDevs => {
            if (pagedDevs) {
                this.pagedDevs.next(pagedDevs);
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByHostId(index: number, hostCfg: HostConfig): string {
        return hostCfg.hostId;
    }

    trackByDevId(index: number, devCfg: DeviceConfig): string {
        return devCfg.devId;
    }

    onClickAddHost() {
        const hosts = this.hosts.map(host => host.hostId);
        this.dialog.open(this.addHostDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank, validateNotDuplicate(hosts)])
            },
            maxWidth: `${HOST_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addHost(id);
            }
        });
    }

    onClickRenameHost(hostId: string) {
        const hosts = this.hosts.map(host => host.hostId).filter(id => id !== hostId);
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].host === hostId);
        this.dialog.open(this.renameHostDialog, {
            data: {
                hostId,
                connectedDevs: connectedDevs.map(dev => dev.devId),
                idFormControl: new FormControl(hostId, [validateNotBlank, validateNotDuplicate(hosts)])
            },
            maxWidth: `${HOST_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== hostId) {
                this.renameHost(hostId, newName, connectedDevs);
            }
        });
    }

    onClickDeleteHost(hostId: string) {
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].host === hostId);
        this.dialog.open(this.deleteHostDialog, {
            data: {
                hostId,
                connectedDevs: connectedDevs.map(dev => dev.devId)
            },
            maxWidth: `${HOST_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteHost(hostId, connectedDevs);
            }
        });
    }

    onClickAddDevice() {
        this.dialog.open(this.addDeviceDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank], [this.mkDeviceUniquenessValidator()])
            },
            maxWidth: `${DEVICE_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addDevice(id);
            }
        });
    }

    onClickRenameDevice(devId: string) {
        this.dialog.open(this.renameDeviceDialog, {
            data: {
                devId,
                idFormControl: new FormControl(devId, [validateNotBlank], [this.mkDeviceUniquenessValidator(devId)])
            },
            maxWidth: `${DEVICE_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== devId) {
                this.renameDevice(devId, newName);
            }
        });
    }

    onClickDeleteDevice(devId: string) {
        this.dialog.open(this.deleteDeviceDialog, {
            data: {
                devId
            },
            maxWidth: `${DEVICE_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteDevice(devId);
            }
        });
    }

    onClickDevicePollingPolicy(devId: string) {
        this.dialog.open(PollingPolicyComponent, {
            data: {
                devId,
                dev: this.cfg.devices[devId]
            } as PollingPolicyData,
            maxWidth: `${DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((policy: PollingPolicy) => {
            if (policy) {
                this.setDevicePolicy(devId, policy);
            }
        });
    }

    onClickDeviceAdvancedSettings(dev: DeviceConfig) {
        const reference = this.cfg.devices[dev.devId].reference;
        const references = _.isArray(reference) ? reference : (!!reference ? [reference] : []);
        const form = this.fb.group({
            dataType: new FormControl(this.cfg.devices[dev.devId].data_type, [Validators.required]),
            refType: new FormControl(this.cfg.devices[dev.devId].reference_type, [Validators.required]),
            expression: new FormControl(this.cfg.devices[dev.devId].expression, [
                (ctrl: AbstractControl) => this.validateExpressionAgainstReference(ctrl, dev)
            ])
        });
        this.dialog.open(this.advancedDeviceSettingsDialog, {
            data: {
                devId: dev.devId,
                form,
                references,
                features: this.mkEditorFeatures(references)
            },
            maxWidth: `${DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setDeviceAdvancedSettings(dev,
                    form.controls.dataType.value,
                    form.controls.refType.value,
                    form.controls.expression.value);
            }
        });
    }

    private get isValid(): boolean {
        return this.form.valid &&
            this.hosts.every(host => host.form.valid) &&
            this.devices.every(dev => dev.form.valid);
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private mkEditorFeatures(references: string[]): Features {
        const cfg = this.appCfg.exprEditor;
        return {
            keywords: _.fromPairs([
                ...EDITOR_BUILTIN_TYPES.map(t => [t, {color: cfg.builtinTypesColor, bold: cfg.builtinsBold}]),
                ...EDITOR_BUILTIN_FUNCTIONS.map(f => [f, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}]),
                ...VARIABLE_ACCESSORS.map(f => [f, {
                    color: cfg.variableAccessorsColor,
                    bold: cfg.variableAccessorBold
                }]),
                ...references.map(m => [m, {color: cfg.variablesColor, bold: cfg.variablesBold}])
            ])
        };
    }

    private addHost(hostId: string) {
        // config must be updated in order for mkHostConfig() to work
        this.cfg.hosts[hostId] = {
            host: null,
            port: IEC61850_DEFAULT_HOST_PORT
        };
        this.hosts.push(this.mkHostConfig(hostId));
        this.hosts.sort(keySorter('hostId'));
        this.reloadHostsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameHost(hostId: string, newName: string, connectedDevs: DeviceConfig[]) {
        const host = this.hosts.find(host => host.hostId === hostId);
        host.hostId = newName;
        this.cfg.hosts[newName] = this.cfg.hosts[hostId];
        delete this.cfg.hosts[hostId];
        this.hosts.sort(keySorter('hostId'));
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.hostId.setValue(newName, {emitEvent: false});
            this.cfg.devices[dev.devId].host = newName;
        });
        this.reloadHostsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteHost(hostId: string, connectedDevs: DeviceConfig[]) {
        const idx = this.hosts.findIndex(host => host.hostId === hostId);
        this.hosts[idx].valueSubscription.unsubscribe();
        this.hosts[idx].statusSubscription.unsubscribe();
        this.hosts.splice(idx, 1);
        delete this.cfg.hosts[hostId];
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.hostId.setValue(null, {emitEvent: false});
            dev.form.controls.hostId.markAsTouched();
            this.cfg.devices[dev.devId].host = null;
        });
        this.reloadHostsPages.next();
        this.updateConfig();
        // validity may change if there are some affected devices
        if (connectedDevs.length) {
            this.updateValidity();
        }
    }

    private addDevice(devId: string) {
        // config must be updated in order for mkDeviceConfig() to work
        this.cfg.devices[devId] = {
            host: null,
            reference: null,
            interval: DEFAULT_POLLING_INTERVAL,
            data_type: IEC61850_DEFAULT_DEVICE_DATA_TYPE,
            reference_type: IEC61850_DEFAULT_DEVICE_REF_TYPE
        };
        this.devices.push(this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameDevice(devId: string, newName: string) {
        const dev = this.devices.find(dev => dev.devId === devId);
        dev.devId = newName;
        this.cfg.devices[newName] = this.cfg.devices[devId];
        delete this.cfg.devices[devId];
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteDevice(devId: string) {
        const idx = this.devices.findIndex(dev => dev.devId === devId);
        this.devices[idx].valueSubscription.unsubscribe();
        this.devices[idx].statusSubscription.unsubscribe();
        this.devices.splice(idx, 1);
        delete this.cfg.devices[devId];
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity may change (e.g. in case an invalid device is deleted)
        this.updateValidity();
    }

    private setDevicePolicy(devId: string, policy: PollingPolicy) {
        this.cfg.devices[devId] = setDevicePolicy(this.cfg.devices[devId], policy);
        this.updateConfig();
        // `policy` is assumed to contain valid data, so no need for validity update
    }

    private setDeviceAdvancedSettings(dev: DeviceConfig,
                                      dataType: DataType,
                                      refType: RefType,
                                      expression: string) {
        this.cfg.devices[dev.devId].data_type = dataType;
        this.cfg.devices[dev.devId].reference_type = refType;
        this.cfg.devices[dev.devId].expression = expression && expression.trim().length ? expression.trim() : null;
        this.updateConfig();
        // validity may have changed (e.g. an expression may just have been set for a multi-reference device)
        dev.form.controls.reference.updateValueAndValidity(); // automatically triggers a status update
    }

    private mkHostConfig(hostId: string): HostConfig {
        const cfg = {
            hostId: hostId,
            form: this.fb.group({
                host: new FormControl(this.cfg.hosts[hostId].host, [validateNotBlank]),
                port: new FormControl(this.cfg.hosts[hostId].port, [Validators.required, validateInteger, validateGt(0)])
            })
        } as HostConfig;
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const host = this.cfg.hosts[cfg.hostId];
            host.host = value['host'];
            host.port = value['port'];
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceConfig(devId: string): DeviceConfig {
        const cfg = {
            devId: devId,
            form: this.fb.group({
                hostId: new FormControl(this.cfg.devices[devId].host, [Validators.required]),
                reference: new FormControl(referenceToForm(this.cfg.devices[devId].reference), []),
                uom: new FormControl(this.cfg.devices[devId].uom, [])
            })
        } as DeviceConfig;
        // NOTE: it is crucial that `cfg` reference does not change at run-time, so that reference validator is always able
        //       to access the device config structure, where the (potentially renamed) device id is stored
        cfg.form.controls.reference.setValidators([
            validateNotBlankTrim([' ', ',']),
            (ctrl: AbstractControl) => this.validateReferenceAgainstExpression(ctrl, cfg)
        ]);
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const dev = this.cfg.devices[cfg.devId];
            dev.host = value['hostId'] ? value['hostId'].trim() : null;
            dev.reference = formToReference(value['reference']);
            dev.uom = value['uom'] && value['uom'].trim().length ? value['uom'].trim() : null;
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceUniquenessValidator(ignoreDevice?: string): AsyncValidatorFn {
        return ctrl => {
            const device = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            if (!device || (!!ignoreDevice && device === ignoreDevice)) {
                return of(null);
            }
            return this.isDeviceDuplicate(device).pipe(
                map(isDuplicate => isDuplicate ? {'duplicate': true} : null)
            );
        };
    }

    private isDeviceDuplicate(device: string): Observable<boolean> {
        // check for duplicates inside this very connector, using *current* values
        if (this.devices.find(devCfg => devCfg.devId === device)) {
            return of(true);
        }
        return this.apiService.checkConnectorDeviceExistence(device).pipe(
            switchMap(resp => resp.value ?
                this.apiService.findConnectorByDevice(device).pipe(
                    map(idResp => idResp?.id !== this.id) // ignore "this very connector" (already checked against current values)
                ) :
                of(false))
        );
    }

    private validateReferenceAgainstExpression(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const reference = formToReference(ctrl.value);
        if (_.isArray(reference) && reference.length > 1) {
            const expr = this.cfg.devices[dev.devId].expression;
            if (!expr || !expr.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private validateExpressionAgainstReference(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const reference = this.cfg.devices[dev.devId].reference;
        if (_.isArray(reference) && reference.length > 1) {
            if (!ctrl.value || !ctrl.value.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private mkPagedHosts(size: number, page: number): Observable<PagedHosts> {
        let pagedHosts = [];
        for (let i = 0; i < this.hosts.length; i += size) {
            pagedHosts.push(this.hosts.slice(i, i + size));
        }
        return of({
            data: pagedHosts[page],
            total: this.hosts.length
        } as PagedHosts);
    }

    private mkPagedDevices(size: number, page: number): Observable<PagedDevices> {
        let pagedDevs = [];
        for (let i = 0; i < this.devices.length; i += size) {
            pagedDevs.push(this.devices.slice(i, i + size));
        }
        return of({
            data: pagedDevs[page],
            total: this.devices.length
        } as PagedDevices);
    }
}

function referenceToForm(reference: string | string[]): string {
    if (!reference) {
        return null;
    }
    if (_.isArray(reference)) {
        return reference.join(', ');
    }
    return reference;
}

function formToReference(reference: string): string | string[] {
    if (!reference) {
        return null;
    }
    const references = reference.split(',').map(p => p.trim());
    return references.length > 1 ? references : references[0];
}
